import * as React from 'react';

import { LayoutTriggers } from 'defs/layout';
import routes from 'defs/routes';
import i18n from 'helper/i18n';
import appConfig from 'helper/mapBackendConfig';
import noop from 'helper/noop';

import { ImageProps } from 'components/atoms/Image';
import { LinkProps } from 'components/atoms/Link';
import Loader from 'components/atoms/Loader';
import BoardStage from 'components/molecules/Board/BoardStage';
import Onboarding from 'components/molecules/Onboarding/Standalone';
import TextBox from 'components/molecules/TextBox';
import BoardStats from 'containers/molecules/Dashboard/BoardStats';
import BoardTrend from 'containers/molecules/Dashboard/BoardTrend';
import { ContainerDashboardViewPropsType } from 'containers/views/DashboardView';
import { MEASUREMENT_DIAGRAM_TYPE } from 'store/dashboard/dashboard';

export type ProgramType = {
	title: string;
	previewImage: ImageProps;
};

export type OverviewType = {
	currentDay?: {
		date?: {
			date: string;
		};
	};
};

export type DashboardViewPropsType = ContainerDashboardViewPropsType;

type DashboardViewStateType = {
	showOnboarding: boolean;
};

const modifyButtonLinks = (buttons: LinkProps[]): LinkProps[] => {
	return buttons.map((button: LinkProps) => {
		return {
			...button,
			href: void 0,
			externalHref: button.href || '',
			modifier: 'button-green',
		};
	});
};

export class DashboardView extends React.Component<DashboardViewPropsType, DashboardViewStateType> {
	public static displayName = 'DashboardView';

	public static defaultProps = {
		doRequestDashboard: noop,
	};

	public state = {
		showOnboarding: false,
	};

	public tracked = false;

	public componentDidMount() {
		if (!this.props.isSequenceLoading) {
			this.props.doRequestDashboard();
			this.props.doRequestMeasurementDiagram(MEASUREMENT_DIAGRAM_TYPE.WEIGHT as any);
		}
		this.props.doRestoreLastScrollPosition();
	}

	public toggleOnboarding = () => {
		const onboardingIsVisible = this.state.showOnboarding;

		if (!onboardingIsVisible) {
			this.props.doLockBody(LayoutTriggers.Onboarding);
		}

		this.setState(
			{
				showOnboarding: !onboardingIsVisible,
			},
			() => {
				if (onboardingIsVisible) {
					this.props.doUnlockBody(LayoutTriggers.Onboarding);
				}
			}
		);
	};

	public renderHelpLinks() {
		const tutorialLink = {
			title: i18n('react.impulsCoach.views.dashboard.links.tutorial'),
			onClick: this.toggleOnboarding,
		};
		const updateDataLink = {
			title: i18n('react.impulsCoach.views.dashboard.links.data'),
			href: routes.profile.indexRoute.href,
			modifier: 'button-green',
		};

		return (
			<div>
				<TextBox
					title={i18n('react.impulsCoach.views.dashboard.faq.title')}
					text={i18n('react.impulsCoach.views.dashboard.faq.text')}
					buttons={modifyButtonLinks([tutorialLink, ...appConfig.views.Dashboard.links])}
				/>

				<TextBox
					title={i18n('react.impulsCoach.views.dashboard.changes.title')}
					text={i18n('react.impulsCoach.views.dashboard.changes.text')}
					buttons={[updateDataLink]}
				/>
			</div>
		);
	}

	public render() {
		const { program, todos, overview, previewImage }: ContainerDashboardViewPropsType = this.props;
		const { showOnboarding } = this.state;

		if (!overview) {
			return <Loader />;
		}

		if (!this.tracked) {
			this.props.trackVirtualPageView({});
			this.tracked = true;
		}

		if (!program) {
			const newProgramLink = {
				title: i18n('react.impulsCoach.views.dashboard.noProgram.button'),
				href: routes.program.recommended.href,
			};

			return (
				<React.Fragment>
					<Onboarding visible={showOnboarding} delegate={this.toggleOnboarding} />
					<TextBox
						coach={this.props.coach ? this.props.coach : 'LEO'}
						modifier="no-program"
						bevel="bottom-right"
						title={i18n('react.impulsCoach.views.dashboard.noProgram.title')}
						text={i18n('react.impulsCoach.views.dashboard.noProgram.text')}
						buttons={[
							{
								...newProgramLink,
								externalHref: void 0,
								modifier: 'button-green',
							},
						]}
					/>
					<BoardTrend />
					{this.renderHelpLinks()}
				</React.Fragment>
			);
		}

		const currentDate: string = ((overview.currentDay || {}).date || {}).date || '';

		return (
			<React.Fragment>
				<Onboarding visible={showOnboarding} delegate={this.toggleOnboarding} />
				<BoardStage
					currentDate={currentDate}
					title={(program as any).title}
					image={previewImage as ImageProps}
					todos={todos}
				/>
				<BoardStats />
				<BoardTrend />
				{this.renderHelpLinks()}
			</React.Fragment>
		);
	}
}

export default DashboardView;
